import { createContext, PropsWithChildren, useState } from "react";

import { AssetInfo, AuthenticatedUser } from "~/entities/authEntities";
import { CustomerSignInOptions } from "~/entities/baseEntities";
import MyAccountModal from "~/modules/auth/containers/MyAccountModal/MyAccountModal";

const isAuthEnabled = (
  customerSignInOptions?: CustomerSignInOptions
): boolean => {
  if (!customerSignInOptions) return false;

  return [
    ...Object.values(customerSignInOptions.passwordless),
    ...Object.values(customerSignInOptions.signInWith),
  ].some((value) => value);
};

interface AuthContextDataProps {
  authenticatedUser?: AuthenticatedUser;
  requiresSignup?: boolean;
  customerSignInOptions?: CustomerSignInOptions;
  useComoAuth?: boolean;
  comoRequiresSignup?: boolean;
  verifiedEmail?: string;
  verifiedPhone?: string;
  assetInfo?: Promise<AssetInfo & { error?: string }>;
}

interface AuthContextData extends AuthContextDataProps {
  isAuthEnabled: boolean;
  openMyAccountModal: () => void;
  isAuthDialogOpen: boolean;
  setIsAuthDialogOpen: (open: boolean) => void;
}

const AuthContext = createContext<AuthContextData>({
  authenticatedUser: undefined,
  requiresSignup: false,
  customerSignInOptions: undefined,
  isAuthEnabled: false,
  verifiedEmail: "",
  verifiedPhone: "",
  openMyAccountModal: () => null,
  isAuthDialogOpen: false,
  setIsAuthDialogOpen: () => null,
});

export const AuthContextProvider = ({
  children,
  authenticatedUser,
  requiresSignup,
  customerSignInOptions,
  useComoAuth,
  comoRequiresSignup,
  verifiedEmail,
  verifiedPhone,
  assetInfo,
}: PropsWithChildren<AuthContextDataProps>) => {
  const [showMyAccountModal, setShowMyAccountModal] = useState(false);
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);

  return (
    <AuthContext.Provider
      value={{
        authenticatedUser,
        requiresSignup,
        customerSignInOptions,
        useComoAuth,
        comoRequiresSignup,
        isAuthEnabled: useComoAuth || isAuthEnabled(customerSignInOptions),
        verifiedEmail,
        verifiedPhone,
        openMyAccountModal: () => setShowMyAccountModal(true),
        assetInfo,
        isAuthDialogOpen,
        setIsAuthDialogOpen,
      }}
    >
      <MyAccountModal
        visible={showMyAccountModal}
        onClose={() => setShowMyAccountModal(false)}
      />
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
